/**
 * Interest forms on the product pages of techforcerobotics.com (TIM-E, RaaSP,
 * RUN-R and BRON-E), posted to /api/public/product-inquiries/<form> with the same
 * API key as the other website forms.
 */

import { API_BASE } from '../config/apiBase';
import type { CampaignAttribution } from './campaignAttribution';

export type ProductInquiryProduct = 'tim-e' | 'raasp' | 'run-r' | 'bron-e';

export interface ProductInquiry extends CampaignAttribution {
  id: number;
  product: ProductInquiryProduct;
  first_name: string;
  last_name: string;
  company: string;
  email: string;
  phone: string;
  sms_marketing_consent: boolean;
  industry: string;
  facility_location: string;
  property_type: string;
  facility_type: string;
  estimated_payload: string;
  /** The form's checkbox list: what to transport, automate or deliver. */
  use_cases: string[];
  needs: string;
  status: string;
  created_at: string | null;
}

/** How a form's answers are labelled, and which of the shared questions it asks. */
export interface ProductInquiryFormInfo {
  label: string;
  useCasesLabel: string;
  needsLabel: string | null;
  asksIndustry: boolean;
  asksFacilityLocation: boolean;
  asksPropertyType: boolean;
  asksFacilityType: boolean;
  asksEstimatedPayload: boolean;
  asksSmsConsent: boolean;
}

export const PRODUCT_INQUIRY_FORMS: Record<ProductInquiryProduct, ProductInquiryFormInfo> = {
  'tim-e': {
    label: 'TIM-E',
    useCasesLabel: 'Transport',
    needsLabel: 'Operational needs',
    asksIndustry: true,
    asksFacilityLocation: true,
    asksPropertyType: false,
    asksFacilityType: false,
    asksEstimatedPayload: false,
    asksSmsConsent: false,
  },
  raasp: {
    label: 'RaaSP',
    useCasesLabel: 'Workflows to automate',
    needsLabel: null,
    asksIndustry: true,
    asksFacilityLocation: false,
    asksPropertyType: false,
    asksFacilityType: false,
    asksEstimatedPayload: false,
    asksSmsConsent: true,
  },
  'run-r': {
    label: 'RUN-R',
    useCasesLabel: 'Deliveries',
    needsLabel: 'Guest delivery needs',
    asksIndustry: false,
    asksFacilityLocation: true,
    asksPropertyType: true,
    asksFacilityType: false,
    asksEstimatedPayload: false,
    asksSmsConsent: true,
  },
  'bron-e': {
    label: 'BRON-E',
    useCasesLabel: 'Transport',
    needsLabel: 'Transport workflow',
    asksIndustry: true,
    asksFacilityLocation: true,
    asksPropertyType: false,
    asksFacilityType: true,
    asksEstimatedPayload: true,
    asksSmsConsent: false,
  },
};

export const PRODUCT_INQUIRY_PRODUCTS = Object.keys(PRODUCT_INQUIRY_FORMS) as ProductInquiryProduct[];

/** Rows from a form this build doesn't know yet still render, with every question shown. */
export function productInquiryForm(product: string): ProductInquiryFormInfo {
  return (
    (PRODUCT_INQUIRY_FORMS as Record<string, ProductInquiryFormInfo | undefined>)[product] ?? {
      label: product.toUpperCase(),
      useCasesLabel: 'Interested in',
      needsLabel: 'Needs',
      asksIndustry: true,
      asksFacilityLocation: true,
      asksPropertyType: true,
      asksFacilityType: true,
      asksEstimatedPayload: true,
      asksSmsConsent: true,
    }
  );
}

export async function fetchProductInquiries(userId: number): Promise<ProductInquiry[]> {
  const res = await fetch(
    `${API_BASE}/api/marketing/product-inquiries?userId=${encodeURIComponent(userId)}`
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data as { error?: string }).error || 'Failed to load product inquiries');
  }
  return (data as { inquiries?: ProductInquiry[] }).inquiries ?? [];
}
